import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { clearCardNumberValidation } from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';
import { ServicingBaseClass } from '@common/components';
import { isEmptyObject, isStringEmpty, parseEmptyArray, stringEmptyDefault } from '@common/helpers';
import {
  PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT,
  PaymentMethodsType,
  REMOVE_VALIDATIONS_FORMS,
} from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { ServicingCommonStoreService } from '@common/index';
import { MCountryI18n, MListData, PhoneNumberCountryType } from '@common/interfaces';
import { CountryI18nBy2LetterCode } from '@common/interfaces/master/country_i18n_by2lettercode';
import { CountryI18nPosCountry } from '@common/interfaces/master/country_i18n_pos_country';
import { MChargeableService } from '@common/interfaces/master/m_chargeable_service';
import { MPaymentError } from '@common/interfaces/master/m_payment_error';
import { MPaymentInformation } from '@common/interfaces/master/m_payment_information';
import { PosCountryAll } from '@common/interfaces/master/pos_county_all';
import {
  ChargeableLoungePassengerApplyStatusMap,
  FirstBaggageBoundMap,
  FirstBaggagePassengerApplyInfo,
  MealPassengerApplyInfo,
  MealPassengerApplyStatusMap,
} from '@common/interfaces/mybooking/mybooking.interface';
import { PaymentDetailsData } from '@common/interfaces/reservation-basic-information-input/reservation-basic-information-input.interface';
import {
  ApiResponse,
  INITIAL_MASTER_DATA,
  MasterDataS02P020,
  PassengerSMSInformation,
  ServiceRequestPaymentInputParams,
} from '@common/interfaces/service-request-payment-input/service-request-payment-input.interface';
import { SegmentMap } from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import {
  AlertMessageItem,
  ApiCommonRequest,
  ApiErrorResponseModel,
  ApiErrorsInner,
  AswServiceModel,
  DeviceType,
  ErrorType,
  LoginStatusType,
  PageType,
} from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { AswServiceState } from '@lib/store';
import { BehaviorSubject, firstValueFrom, take } from 'rxjs';
import {
  AmcmemberApiService,
  GetBasicReservationInformationRequest,
  GetBasicReservationInformationResponseData,
} from 'src/sdk-amcmember';
import {
  Bound,
  GetOrderRequest,
  GetOrderResponseData,
  DefaultService as ServicingApiService,
  Traveler,
  UpdateServicesRequest,
  UpdateServicesResponse,
} from 'src/sdk-servicing';
import { FlightMealService } from '../my-booking/flight-meal-service/flight-meal-service';
import { ErrorCodeConstants } from '@conf/app.constants';

interface UpdateServicesWarningSourceItem {
  flightId?: string;
  travelerIds?: string[];
  code?: string;
}

interface UpdateServicesWarningSource {
  items?: UpdateServicesWarningSourceItem[];
}

@Injectable({ providedIn: 'root' })
export class ServiceRequestPaymentInputService extends ServicingBaseClass {
  public transitionSourceFullFunctionPageId: string = '';
  // 遷移元機能ID
  public transitionSourceFunctionId: string = '';
  // 遷移元画面ID
  public transitionSourcePageId: string = '';

  // 利用可能カードブランドパターン
  public cardBrandPattern: string = '';
  // 支払情報 (ASWDB)
  public paymentInformation: Array<MPaymentInformation> = [];
  // 決済エラー (ASWDB)
  public paymentErrors: Array<MPaymentError> = [];
  // 国 (ASWDB)
  public countries: Array<CountryI18nBy2LetterCode> = [];
  // 国(国際化) (ASWDB)
  public i18nCountries: Array<MCountryI18n> = [];
  // POS管轄国 (ASWDB)
  public posCountries: Array<PosCountryAll> = [];
  // 汎用マスターデータ（リスト） (ASWDB)
  public listData: Array<MListData> = [];
  // 有料サービス情報 (ASWDB)
  public chargeableService: Array<MChargeableService> = [];
  cardHolderCountryPhoneExtension: string = '';
  phoneCountryCode: string = '';

  paymentDetailsList: PaymentDetailsData[] = [];
  // 搭乗者SMS送信選択初期値
  smsTransmissionPreference?: boolean;
  ordersPaymentRecordsReqParam: UpdateServicesRequest = {} as UpdateServicesRequest;

  public pnr$ = new BehaviorSubject<GetOrderResponseData>({});
  public reRenderCreditCard$ = new BehaviorSubject<boolean>(false);
  public amc$ = new BehaviorSubject<GetBasicReservationInformationResponseData>({});
  serviceRequestPaymentInputMasterState$ = new BehaviorSubject<MasterDataS02P020>(INITIAL_MASTER_DATA);
  public paymentMethodDisplayList$ = new BehaviorSubject<{ [key: string]: { value: boolean } }>({});

  public paymentMethod$ = new BehaviorSubject<string>(PaymentMethodsType.CREDIT_CARD);
  public previousPaymentMethod: string = '';

  public checkValid$ = new BehaviorSubject<boolean>(false);

  public travelerCanEditInputNotificationSMS$ = new BehaviorSubject(new Set<string>());

  firstBaggageBoundInforList: Bound[] = [];
  firstBaggagePassengerInforList: Traveler[] = [];
  loungeFlightInforList: Bound[] = [];
  loungePassengerInforList: Traveler[] = [];
  mealFlightInforList: Bound[] = [];
  mealPassengerInforList: Traveler[] = [];
  seatFlightInforList: Bound[] = [];
  seatPassengerInforList: Traveler[] = [];

  onInitCreditCardSubject = new BehaviorSubject(1);

  // dynamic message
  dynamicSubject = new BehaviorSubject<ServiceRequestPaymentInputParams>({});

  isInitEnd$ = new BehaviorSubject<boolean>(false);

  public paymentInputFG!: FormGroup;

  public isSubHeaderDisplayGoBack: boolean = true;

  // (1.11.1) 変数を作成する。
  // (1.11.1.1) 電話番号国リスト(管轄国・日本)を作成する。
  jpPhoneNumberCountryList: Array<PhoneNumberCountryType> = [];

  // (1.11.1.2) 電話番号国リスト(管轄国・日本以外)を作成する。
  public noJpPhoneNumberCountryList: Array<PhoneNumberCountryType> = [];

  /**
   * [リスト定義No.3] 電話番号の国番号選択
   * [リスト定義No.4] 搭乗者SMS国選択
   */
  public phoneNumberCountryList: Array<PhoneNumberCountryType> = [];

  // (1.11.1.3) 電話番号対象国(マップ)を作成する。
  public countryMap: Map<string, string> = new Map<string, string>();

  // パスワード認証実施済み判定
  public isPasswordAuthenticationCompleted: boolean = false;

  // 7.2.	NHグループ運航でアメリカ行き便有無を設定する。
  isNhGroupOperatedAndArrivalUs: boolean = false;

  // 選択済みの使用するクレジットカード
  isSelectedCardSP: boolean = false;

  // 新しいカード
  public selectedCard: string = '';

  public currentYmd: Date = new Date();

  public isDisplayCardNumberAsTextBox: boolean = true;

  // [2.2] 支払総額
  public totalPrice: number = 0;

  private readonly ERROR_API_CODE_MAP: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000257]: 'E0419',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000252]: 'E1142',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000253]: 'E1813',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000434]: 'E0525',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000264]: 'E0379',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000262]: 'E1814',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000396]: 'E0519',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000270]: 'E0563',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000778]: 'E1819',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000271]: 'E1143',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000268]: 'E0113',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000269]: 'E0114',
  };

  private readonly WARNING_API_CODE_MAP: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.WBAZ000218]: 'W0742',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000563]: 'E0426',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000562]: 'E0422',
  };

  private readonly SERVICE_SEAT_API_CODE_MAP: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.WBAZ000253]: 'E0443',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000355]: 'W0444',
  };

  private readonly NOT_RETRYABLE_API_CODE_MAP: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000246]: 'E1791',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000386]: 'E1792',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000247]: 'E1793',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000248]: 'E1794',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000251]: 'E1795',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000254]: 'E1354',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000380]: 'E1796',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000255]: 'E1797',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000256]: 'E1798',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000433]: 'E1799',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000258]: 'E1800',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000259]: 'E1801',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000260]: 'E1802',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000261]: 'E1803',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000263]: 'E1804',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000265]: 'E1805',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000266]: 'E1806',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000267]: 'E1807',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000271]: 'E1143',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000724]: 'E1808',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000822]: 'E1828',
  };

  private readonly SYSTEM_ERROR_API_CODE_MAP: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.ECLZ000001]: '',
    [ErrorCodeConstants.ERROR_CODES.ECLZ000002]: '',
    [ErrorCodeConstants.ERROR_CODES.ECLZ000003]: '',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000155]: '',
  };

  constructor(
    private readonly _common: CommonLibService,
    private readonly _staticMsg: StaticMsgPipe,
    private readonly _servicingApiService: ServicingApiService,
    private readonly _amcmemberApiService: AmcmemberApiService,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _flightMealService: FlightMealService
  ) {
    super();
  }

  // Call Event (1)
  runOnInitCreditCard() {
    this.onInitCreditCardSubject.next(this.onInitCreditCardSubject.value + 1);
  }

  /**
   * (共通No.1) パスワード入力の認証ボタン後処理
   * @param value @see RegisteredCardTypeEnum
   */

  handleCommonNo1() {
    // clear card number validation
    clearCardNumberValidation(this.paymentInputFG);
    // (共通No.1.1) パスワード認証実施済み判定をtrue(認証済)に設定する。
    this.isPasswordAuthenticationCompleted = true;
    // (共通No.1.2)「表示内容」シートに従い、画面.クレジットカードの支払い方法の詳細エリアを再表示する。
    this.runOnInitCreditCard();
    // (共通No.1.3) ユーザ共通情報.端末種別=”SP"の場合、画面.使用するクレジットカード選択を非表示とする。
    if (
      this._common.aswContextStoreService.aswContextData.deviceType === DeviceType.SMART_PHONE &&
      this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN
    ) {
      this.isSelectedCardSP = true;
    }
  }

  /** (共通No.2) パスワード入力の閉じるボタン後処理 */
  handleCommonNo2() {
    this.selectedCard = PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD;
  }

  // (共通No.3) 利用可能カードブランドパターン取得処理
  handleCommonNo3(): string {
    // (共通No.3.1) 利用可能カードブランドパターンに""(空欄)を設定する。
    let availableCardBrandPatterns = '';
    // (共通No.3.2) 以下の条件でASWDB(マスタ)のオフィステーブルより利用可能カードブランドパターンを取得する。
    const operationOfficeCode = this._common.aswContextStoreService.aswContextData.pointOfSaleId;
    // オフィス.オフィスコード = ユーザ共通情報.操作オフィスコード
    const currentOffice =
      this.serviceRequestPaymentInputMasterState$.value.officeByOffceCodeInfo?.[String(operationOfficeCode)];
    // (共通No.3.3) 取得できた場合、利用可能カードブランドパターンにオフィス.利用可能カードブランドパターンを設定する。
    if (currentOffice) {
      availableCardBrandPatterns = currentOffice?.[0]?.card_brand_pattern ?? '';
    }
    return availableCardBrandPatterns;
  }

  /**
   * (共通No.5) サービス情報登録API実行結果処理
   * @param response サービス情報登録API応答
   * @returns 処理結果を返却する。
   */
  handleCommonNo5(response: ApiResponse): number {
    // (共通No.5.1) 処理結果に0(正常終了)を設定する。
    let result: number = 0;

    // (共通No.5.2) 以下のエラーまたは警告が返却された場合、当画面にてエラーメッセージを表示する。
    const errors = response.error?.['error'] ?? {};
    const model = response.model ?? {};

    if ('errors' in errors) {
      result = this.handleCommonNo5Error(parseEmptyArray(errors.errors));
    } else if ('warnings' in model) {
      // (共通No.5.2.2) 入力パラメータ.サービス情報登録API応答.warningsを繰り返し、当該warnings.codeは以下になるかチェックする。
      result = this.handleCommonNo5Warning(model);
    }

    // (共通No.5.4) 処理結果を返却する。
    return result;
  }

  /**
   * (共通No.5) サービス情報登録API実行結果処理
   * (共通No.5.2.2) 入力パラメータ.サービス情報登録API応答.warningsを繰り返し、当該warnings.codeは以下になるかチェックする。
   * @param response サービス情報登録API応答
   * @returns 0(正常終了), -1(異常終了)
   */
  handleCommonNo5Warning(response: UpdateServicesResponse): number {
    let result: number = 0;
    let errorMsgId: string = '';
    // (共通No.5.2.2.1.1) 座席登録失敗リストを空で作成する。
    let seatRegistrationErrors: Array<{ flightId: string; travelerId: string; warningCode: string }> = [];
    // (共通No.5.2.2.1.2) サービス登録失敗リストを空で作成する。
    let serviceRegistrationErrors: Array<{ flightId: string; travelerId: string; code: string; warningCode: string }> =
      [];
    const seatRegistrationPlaceholderMap = new Map<string, string>();
    const warnings: AlertMessageItem[] = [];

    if (response.data?.orderStatus === 'ticketed') {
      return result;
    }

    response.warnings?.forEach((warning) => {
      // (共通No.5.2.2.2) いずれのエラーコードの行に応じたメッセージを記載する。
      errorMsgId = stringEmptyDefault(this.WARNING_API_CODE_MAP[warning?.code ?? '']);
      if (!isStringEmpty(errorMsgId)) {
        warnings.push({
          apiErrorCode: warning?.code ?? '',
          errorMessageId: errorMsgId,
          isCloseEnable: false,
          contentHtml: errorMsgId,
        });
      }

      const warningSource = (warning?.source ?? {}) as UpdateServicesWarningSource;

      // (共通No.5.2.2.3) 当該warnings.code = "WBAZ000253"(座席変更時に選択前の座席の削除は成功したが、その後の座席指定に失敗した旨)の場合
      if (
        warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000253 ||
        warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000355
      ) {
        // 座席登録失敗リストに当該warnings.source.itemsを追加する。 ※items:[{flightId:セグメントID,  travelerId: 搭乗者ID}]
        if (warningSource.items && Array.isArray(warningSource.items)) {
          warningSource.items.forEach((item) => {
            const flightId = item.flightId;

            if (typeof flightId == 'string' && Array.isArray(item.travelerIds)) {
              item.travelerIds.forEach((travelerId: string) => {
                seatRegistrationErrors.push({
                  flightId: flightId,
                  travelerId: travelerId,
                  warningCode: warning.code,
                });
              });
            }
          });
        }
      }

      // (共通No.5.2.2.4) 当該warnings.code = "WBAZ000389"(サービス申込の登録失敗(ステータスHK/KK/HN以外))の場合
      if (
        warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000389 ||
        warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000562
      ) {
        // サービス登録失敗リストに当該warnings.source.itemsを追加する。 ※items:[{flightId:セグメントID,  travelerId: 搭乗者ID, code: SSRコード}]
        if (warningSource.items && Array.isArray(warningSource.items)) {
          warningSource.items.forEach((item) => {
            const flightId = item.flightId;

            if (typeof flightId == 'string' && Array.isArray(item.travelerIds)) {
              item.travelerIds.forEach((travelerId: string) => {
                serviceRegistrationErrors.push({
                  flightId: flightId,
                  travelerId: travelerId,
                  code: item?.code ?? '',
                  warningCode: warning.code,
                });
              });
            }
          });
        }
      }

      // (共通No.5.2.2.5) エラーメッセージIDが存在する場合、処理結果に-1(異常終了)を設定する。
      if (!isStringEmpty(errorMsgId)) {
        result = -1;
      }
    });

    let chargeableLounge = this._servicingCommonStoreService.servicingCommon?.chargeableLounge;
    let firstBaggage = this._servicingCommonStoreService.servicingCommon.firstBaggage;
    let meal = this._servicingCommonStoreService.servicingCommon?.meal;
    let seatReservationStatus = this._servicingCommonStoreService.servicingCommon?.seatReservationStatus;

    const addedCouchKeys = new Set<string>();
    const subtractedCouchKeys = new Set<string>();

    // (共通No.5.2.2.6) 座席登録失敗リスト ≧ 1件 または  サービス登録失敗リスト ≧ 1件の場合
    if (seatRegistrationErrors?.length >= 1 || serviceRegistrationErrors?.length >= 1) {
      // (共通No.5.2.2.6.1) 処理結果に-1(異常終了)を設定する。
      result = -1;

      // (共通No.5.2.2.6.2) 登録成功支払総額に0を設定する。
      let totalPrice: number = 0;

      this.previousPaymentMethod = this.paymentMethod$.value;

      // <以下、PNR情報取得API応答.data.boundsの要素繰り返し>(それぞれ要素が当該bound)
      this.pnr$.value?.air?.bounds?.forEach?.((bound) => {
        // <以下、当該bound.flightsの要素繰り返し>(それぞれ要素が当該flight)
        bound?.flights?.forEach?.((flight) => {
          // <以下、PNR情報取得API応答.data.travelersの要素繰り返し>(それぞれ要素が当該traveler)
          this.pnr$.value?.travelers?.forEach?.((traveler) => {
            /**
             * (共通No.5.2.2.6.3.1)
             * サービシング共通store.ラウンジ情報.<当該flight.id>.<当該traveler.id>である情報を取得して、搭乗者ラウンジ情報として保持する。
             */
            const flightChargeableLounge = chargeableLounge?.[
              String(flight?.id)
            ] as ChargeableLoungePassengerApplyStatusMap;
            const passengerChargeableLounge = flightChargeableLounge?.[String(traveler?.id)];

            // (共通No.5.2.2.6.3.2) 搭乗者ラウンジ情報が存在する場合、
            if (passengerChargeableLounge) {
              const loungeInfoInServiceErrors = serviceRegistrationErrors?.find?.(
                (serviceError) =>
                  serviceError?.flightId === flight?.id &&
                  serviceError?.travelerId === traveler?.id &&
                  serviceError?.code === passengerChargeableLounge?.ssrCode
              );

              // (共通No.5.2.2.6.3.2.1) 当該flight.id、当該traveler.id、搭乗者ラウンジ情報.codeであるレコードはサービス登録失敗リストに存在しない場合、
              if (!loungeInfoInServiceErrors) {
                // (共通No.5.2.2.6.3.2.1.1) 登録成功ラウンジ支払総額 += 搭乗者ラウンジ情報.有料サービス金額
                totalPrice += passengerChargeableLounge?.paidServiceAmount ?? 0;
              }
              // (共通No.5.2.2.6.3.2.2) 当該flight.id、当該traveler.id、搭乗者ラウンジ情報.codeであるレコードはサービス登録失敗リストに存在する場合、
              else {
                // (共通No.5.2.2.6.3.2.2.1) サービシング共通store.ラウンジ情報から搭乗者ラウンジ情報を削除する。
                chargeableLounge = {
                  ...chargeableLounge,
                  [String(flight?.id)]: {
                    ...flightChargeableLounge,
                    [String(traveler?.id)]: undefined,
                  },
                  totalAmount:
                    (chargeableLounge?.totalAmount ?? 0) - (passengerChargeableLounge?.paidServiceAmount ?? 0),
                };
              }
            }

            /**
             * (共通No.5.2.2.6.4.1)
             * サービシング共通store.事前追加手荷物.<当該bound.id>.<セグメントID = 当該flight.id>.<当該traveler.id>である情報を取得して、
             * 搭乗者手荷物情報として保持する。
             */
            const boundFirstBaggage = firstBaggage?.[String(bound?.airBoundId)] as FirstBaggageBoundMap;
            const passengerFirstBaggage = boundFirstBaggage?.[String(traveler?.id)] as FirstBaggagePassengerApplyInfo;

            // (共通No.5.2.2.6.4.2) 搭乗者手荷物情報が存在する場合、
            if (passengerFirstBaggage) {
              const baggageInfoInServiceErrors = serviceRegistrationErrors?.find?.(
                (serviceError) =>
                  serviceError?.flightId === flight?.id &&
                  serviceError?.travelerId === traveler?.id &&
                  serviceError?.code === 'FBAG'
              );

              // (共通No.5.2.2.6.4.2.1) 当該flight.id、当該traveler.id、code="FBAG"であるレコードはサービス登録失敗リストに存在しない場合、
              if (!baggageInfoInServiceErrors) {
                // (共通No.5.2.2.6.4.2.1.1) 登録成功事前追加手荷物支払総額 += 搭乗者手荷物情報.有料サービス金額
                totalPrice += passengerFirstBaggage.paidServiceAmount ?? 0;
              }
              // (共通No.5.2.2.6.4.2.2) 当該flight.id、当該traveler.id、code="FBAG"であるレコードはサービス登録失敗リストに存在する場合、
              else {
                // (共通No.5.2.2.6.4.2.2.1) サービシング共通store.事前追加手荷物から搭乗者手荷物情報を削除する。
                firstBaggage = {
                  ...firstBaggage,
                  [String(bound?.airBoundId)]: {
                    ...boundFirstBaggage,
                    [String(traveler?.id)]: undefined,
                  },
                  totalAmount: (firstBaggage?.totalAmount ?? 0) - (passengerFirstBaggage.paidServiceAmount ?? 0),
                };
              }
            }

            /**
             * (共通No.5.2.2.6.5.1)
             * サービシング共通store.機内食情報.<当該flight.id>.<当該traveler.id>である情報を取得して、搭乗者機内食情報として保持する。
             */
            const flightMeal = meal?.[String(flight?.id)] as MealPassengerApplyStatusMap;
            const passengerMeal = flightMeal?.[String(traveler?.id)] as MealPassengerApplyInfo;

            // (共通No.5.2.2.6.5.2) 搭乗者機内食情報が存在する場合、
            if (passengerMeal) {
              const mealInfoInServiceErrors = serviceRegistrationErrors?.find?.(
                (serviceError) =>
                  serviceError?.flightId === flight?.id &&
                  serviceError?.travelerId === traveler?.id &&
                  (serviceError?.code === passengerMeal.ssrCode ||
                    (!serviceError?.code && serviceError?.warningCode === ErrorCodeConstants.ERROR_CODES.WBAZ000562))
              );

              // (共通No.5.2.2.6.5.2.1) 当該flight.id、当該traveler.id、搭乗者機内食情報.codeであるレコードはサービス登録失敗リストに存在しない場合、
              if (!mealInfoInServiceErrors) {
                // (共通No.5.2.2.6.5.2.1.1) 登録成功機内食情報支払総額+= 搭乗者機内食情報.有料サービス金額
                totalPrice += passengerMeal.paidServiceAmount ?? 0;
              }
              // (共通No.5.2.2.6.5.2.2) 当該flight.id、当該traveler.id、搭乗者機内食情報.codeであるレコードはサービス登録失敗リストに存在する場合、
              else {
                // (共通No.5.2.2.6.5.2.2.1) サービシング共通store.事前座席指定から搭乗者機内食情報を削除する。
                meal = {
                  ...meal,
                  [String(flight?.id)]: {
                    ...flightMeal,
                    [String(traveler?.id)]: undefined,
                  },
                  totalAmount: (meal?.totalAmount ?? 0) - (passengerMeal.paidServiceAmount ?? 0),
                };
              }
            }

            /**
             * (共通No.5.2.2.6.6.1)
             * サービシング共通store.事前座席指定.<当該flight.id>.<当該traveler.id>である情報を取得して、搭乗者座席情報として保持する。
             */
            const flightSeatReservationStatus = seatReservationStatus?.[String(flight?.id)] as SegmentMap;
            const passengerSeatReservationStatus = flightSeatReservationStatus?.[String(traveler?.id)];

            // (共通No.5.2.2.6.6.2) 搭乗者座席情報が存在する場合、
            if (passengerSeatReservationStatus) {
              const seatInfoInSeatErrors = seatRegistrationErrors?.find?.(
                (seatError) => seatError?.flightId === flight?.id && seatError?.travelerId === traveler?.id
              );
              const couchSeatNumber: string | undefined =
                passengerSeatReservationStatus.couchSeatNumber?.[0]?.seatNumber;
              const couchKey: string = `${flight?.id}-${couchSeatNumber}`;

              // (共通No.5.2.2.6.6.2.1) 当該flight.id、当該traveler.idであるレコードは座席登録失敗リストに存在しない場合、
              if (!seatInfoInSeatErrors) {
                // (共通No.5.2.2.6.6.2.1.1) 登録成功事前座席指定支払総額 += 搭乗者座席情報.有料サービス金額
                if (!addedCouchKeys.has(couchKey)) {
                  totalPrice += Number(passengerSeatReservationStatus.paidServiceAmount ?? 0);
                  if (couchSeatNumber) {
                    addedCouchKeys.add(couchKey);
                  }
                }
              }
              // (共通No.5.2.2.6.6.2.2) 当該flight.id、当該traveler.idであるレコードは座席登録失敗リストに存在する場合、
              else {
                // (共通No.5.2.2.6.6.2.2.1) サービシング共通store.機内食情報から搭乗者座席情報を削除する。
                if (!subtractedCouchKeys.has(couchKey)) {
                  seatReservationStatus = {
                    ...seatReservationStatus,
                    [String(flight?.id)]: {
                      ...flightSeatReservationStatus,
                      [String(traveler?.id)]: undefined,
                    },
                    totalAmount:
                      (seatReservationStatus?.totalAmount ?? 0) -
                      Number(passengerSeatReservationStatus.paidServiceAmount ?? 0),
                  };
                  if (couchSeatNumber) {
                    subtractedCouchKeys.add(couchKey);
                  }
                } else {
                  seatReservationStatus = {
                    ...seatReservationStatus,
                    [String(flight?.id)]: {
                      ...flightSeatReservationStatus,
                      [String(traveler?.id)]: undefined,
                    },
                  };
                }

                /**
                 * (共通No.5.2.2.6.6.2.2.2)
                 * プレースホルダに当該flight.marketingAirlineCode ＋
                 * 当該flight.marketingFlightNumber＋" "(空白) ＋
                 * 当該traveler.names[0].firstName + " "(空白) +
                 * 当該traveler.names[0].lastNameを設定する。
                 * プレースホルダ設定例：NH110 Mr.TARO SORANO
                 */
                const flightCode = `${stringEmptyDefault(flight.marketingAirlineCode)}${stringEmptyDefault(
                  flight.marketingFlightNumber
                )}`;
                const key = `${seatInfoInSeatErrors.warningCode}_${
                  this.SERVICE_SEAT_API_CODE_MAP[seatInfoInSeatErrors.warningCode]
                }_${flightCode}`;
                const travelerName = `${stringEmptyDefault(traveler.names?.[0]?.firstName)} ${stringEmptyDefault(
                  traveler.names?.[0]?.lastName
                )}`;

                if (seatRegistrationPlaceholderMap.has(key)) {
                  seatRegistrationPlaceholderMap.set(
                    key,
                    seatRegistrationPlaceholderMap.get(key) +
                      `${this._staticMsg.transform('label.commaSeparater')} ${travelerName}`
                  );
                } else {
                  seatRegistrationPlaceholderMap.set(key, `${flightCode} ${travelerName}`);
                }
              }
            }
          });
        });
      });

      /**
       * (共通No.5.2.2.6.7)
       * エラーメッセージIDが同じについて、"当該flight.marketingAirlineCode＋当該flight.marketingFlightNumber"のプレースホルダが同じである場合、
       * 搭乗者氏名を","の区切りで連結してプレースホルダをとする。
       * 例：	E0443 ※{0}: プレースホルダ= "NH110 Mr.TARO SORANO"
       *     E0443 ※{0}: プレースホルダ= "NH110 Mr.SABURO SORANO"
       * →	 E0443 ※{0}: プレースホルダ="NH110 Mr.TARO SORANO、Mr.SABURO SORANO"
       */
      seatRegistrationPlaceholderMap.forEach((v, k) => {
        const [apiErrorCode, errorMessageId, _] = k.split('_');

        warnings.push({
          apiErrorCode,
          errorMessageId,
          isCloseEnable: false,
          contentHtml: errorMessageId,
          interpolateParams: {
            ['0']: v,
          },
        });
      });

      // (共通No.5.2.2.6.8) 画面.支払総額に登録成功ラウンジ支払総額+登録成功事前追加手荷物支払総額+登録成功機内食情報支払総額+登録成功事前座席指定支払総額を設定する。
      this.totalPrice = totalPrice;

      this._servicingCommonStoreService.updateServicingCommon({
        // (共通No.5.2.2.6.9) サービシング共通store.ラウンジ情報.合計金額に登録成功ラウンジ支払総額を設定する。
        chargeableLounge,
        // (共通No.5.2.2.6.10) サービシング共通store.事前追加手荷物.合計金額に登録成功事前追加手荷物支払総額を設定する。
        firstBaggage,
        // (共通No.5.2.2.6.11) サービシング共通store.機内食情報.合計金額に登録成功機内食情報支払総額を設定する。
        meal,
        // (共通No.5.2.2.6.12) サービシング共通store.事前座席指定.合計金額に登録成功事前座席指定支払総額を設定する。
        seatReservationStatus,
      });

      // (共通No.5.2.2.6.13) サービス登録失敗リストからcodeを全て取得して（重複を除く）サービスID登録失敗SSRコードリストを作成する。
      const ssrCodeSet = serviceRegistrationErrors?.reduce(
        (result: Set<string>, current: { flightId: string; travelerId: string; code: string }) => {
          return result.add(current.code);
        },
        new Set()
      );

      // (共通No.5.2.2.6.14) サービスID登録失敗SSRコードリストが存在する場合、Ancillaryサービス名称を作成する。
      const ancillaryServiceNames: string[] = [];

      ssrCodeSet.forEach((code) => {
        // (共通No.5.2.2.6.14.1) (当該サービスID登録失敗SSRコード="LOUG"、または"FBAG"、または"SIT-")、または(当該サービスID登録失敗SSRコードの先頭2桁 = "CO")の場合、
        if (code === 'LOUG' || code === 'FBAG' || code === 'SIT-' || code.startsWith('CO')) {
          // 当該サービスID登録失敗SSRコード.SSRコードを引数として、「当画面共通処理.Ancillaryサービス名称取得処理」を行い、
          // 返却値のAncillaryサービス名称を名称サービス登録失敗リストに追加する。
          ancillaryServiceNames.push(this.handleCommonNo7(code));
        }
        // (共通No.5.2.2.6.14.2) 上記以外の場合、当該サービスID登録失敗SSRコード.SSRコードを引数として、機内食申込(S02-M017 )の「当画面共通処理.有料機内食名称取得処理」を行い、
        else {
          // 有料機内食名を名称サービス登録失敗リストに追加する。
          ancillaryServiceNames.push(this._flightMealService.onHandleCommonNo3(code));
        }
      });
      // (共通No.5.2.2.6.14.3) 名称サービス登録失敗リスト の要素を区切り文字を使用して連結し、Ancillaryサービス名称を作成します。
      const ancillaryServiceNamePlaceholder = ancillaryServiceNames.join(', ');

      // (共通No.5.2.2.6.14.4) Ancillaryサービス名称≠""の場合、いずれのエラーコードの行に応じたメッセージを記載する。
      if (!isStringEmpty(ancillaryServiceNamePlaceholder)) {
        warnings.push({
          apiErrorCode: ErrorCodeConstants.ERROR_CODES.WBAZ000389,
          errorMessageId: 'W0524',
          isCloseEnable: false,
          contentHtml: 'W0524',
          interpolateParams: {
            ['0']: ancillaryServiceNamePlaceholder,
          },
        });
      }
    }

    // (共通No.5.2.2.7.2) 上記以外の場合、以下の情報を設定して、当画面にてエラーメッセージを表示する。
    warnings.forEach((warning) => {
      this._common.alertMessageStoreService.setAlertWarningMessage(warning);
    });

    return result;
  }

  /**
   * (共通No.5) サービス情報登録API実行結果処理
   * (共通No.5.2.1.1)
   * (共通No.5.2.1.2)
   * (共通No.5.2.1.3)
   * (共通No.5.2.1.4)
   * (共通No.5.3) 以下のエラーまたは警告が返却された場合、共通エラー画面へ遷移し、エラーメッセージを表示する。
   * @param response サービス情報登録API応答.errors
   * @returns 0(正常終了), -1(異常終了)
   */
  handleCommonNo5Error(apiErrorsInner: Array<ApiErrorsInner> = []): number {
    let result: number = 0;
    let errorMsgId: string = '';

    for (let error of apiErrorsInner) {
      // (共通No.5.2.1.1) いずれのエラーコードの行に応じたメッセージを記載する。
      if (this.ERROR_API_CODE_MAP[error.code]) {
        errorMsgId = this.ERROR_API_CODE_MAP[error.code];
      }
      // (共通No.5.2.1.2) エラーコード="EBAZ000269"(クレジットカード決済エラー)の場合、以下の処理を行う。
      // else if (error.code === ErrorCodeConstants.ERROR_CODES.EBAZ000269) {
      //   errorMsgId = this.handleCreditCardPaymentError(error, error.code);
      // }

      // (共通No.5.2.1.3) エラーメッセージIDが存在する場合、処理結果に-1(異常終了)を設定する。
      if (!isStringEmpty(errorMsgId)) {
        result = -1;

        // (共通No.5.2.1.4) エラーメッセージIDが存在する場合、
        // (共通No.5.2.1.4.1) エラーメッセージIDの内に、"E0525"である要素がある場合、 共通エラー画面へ遷移し、エラーメッセージを表示する。以下の情報を設定して、エラーメッセージを表示する。
        if (errorMsgId === 'E0525') {
          this._common.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.BUSINESS_LOGIC,
            errorMsgId: errorMsgId,
            apiErrorCode: stringEmptyDefault(error.code),
          });

          break;
        }
        // (共通No.5.2.1.4.2) 上記以外の場合、以下の情報を設定して、当画面にてエラーメッセージを表示する。
        this._common.errorsHandlerService.setRetryableError(PageType.PAGE, {
          errorMsgId: errorMsgId,
          apiErrorCode: stringEmptyDefault(error.code),
        });
      }

      // (共通No.5.3.1.1) いずれのエラーコードの行に応じたメッセージを記載する。
      // (共通No.5.3.1.2) エラーメッセージIDが存在する場合、以下の情報を設定する。
      if (!isStringEmpty(this.NOT_RETRYABLE_API_CODE_MAP[error.code])) {
        this._common.errorsHandlerService.setNotRetryableError({
          errorType: ErrorType.BUSINESS_LOGIC,
          errorMsgId: this.NOT_RETRYABLE_API_CODE_MAP[error.code],
          apiErrorCode: stringEmptyDefault(error.code),
        });

        result = -1;

        break;
      }

      // (共通No.5.4.1.1)
      // (共通No.5.4.1.2)
      if (this.SYSTEM_ERROR_API_CODE_MAP[error.code] === '') {
        this._common.errorsHandlerService.setNotRetryableError({
          errorType: ErrorType.SYSTEM,
          apiErrorCode: stringEmptyDefault(error.code),
        });

        result = -1;

        break;
      }
    }

    return result;
  }

  /**
   * (共通No.5) サービス情報登録API実行結果処理
   * (共通No.5.2.1.2.1) 以下の条件でASWDB(マスタ)の決済エラーテーブルより決済エラー情報を取得する。
   * @param error 入力パラメータ.サービス情報登録API応答.errorsを繰り返し
   * @returns 処理結果を返却する。
   */
  handleCreditCardPaymentError(error: ApiErrorsInner, errorMsgId: string): string {
    const paymentErrors = this.serviceRequestPaymentInputMasterState$.value?.paymentErrors ?? [];
    // ソート順: 決済エラー.優先順位
    const sortedPaymentErrors = [...paymentErrors].sort((a, b) => b.priority - a.priority);
    const paymentError = sortedPaymentErrors?.find((paymentError) => {
      const sourceItem = (error?.source as any)?.items?.[0];

      if (sourceItem) {
        return paymentError?.space_error_code === errorMsgId && paymentError?.suberror_code === sourceItem;
      }

      return paymentError?.space_error_code === errorMsgId;
    });

    // (共通No.5.2.1.2.2) 決済エラー情報が取得できた場合、エラーメッセージIDに決済エラー情報[0].メッセージIdを設定する
    // (共通No.5.2.1.2.3) 決済エラー情報が取得できなかった場合、エラーメッセージIDに"E1815"を設定する
    return !isEmptyObject(paymentError) ? (paymentError as MPaymentError).message_id : 'E1815';
  }

  // (共通No.6) 搭乗者SMS国選択の初期化処理
  // FIXME: Đã hardcode interface ở sdk. chờ version sdk mới
  handleCommonNo6(travelerContact: any): Array<PassengerSMSInformation> {
    // (共通No.6.1) SMS国を空で作成する。
    let passengerSMSInformations: PassengerSMSInformation[] = [];
    let travelerContactLenth = travelerContact?.phones?.length;
    // (共通No.6.2) 以下に示す順序に従い、搭乗者SMS情報リストにデータを追加する。
    // (共通No.6.2.1) 入力パラメータ.連絡先情報.phones = 0件の場合、
    // 搭乗者SMS情報空リストを返却する
    if (travelerContactLenth === 0) {
      return passengerSMSInformations;
    } else if (travelerContactLenth === 1) {
      // 2.2 入力パラメータ.連絡先情報.phones = 1件の場合
      // 2.2.1.	国番号 = 入力パラメータ.連絡先情報.phones[0].countryPhoneExtensionを設定する。
      // 2.2.2.	電話番号 = 入力パラメータ.連絡先情報.phones[0].numberを設定する。
      // 2.2.3.	搭乗者SMS情報リストに国番号と電話番号であるレコードを追加する。
      const countryPhone: PassengerSMSInformation = {
        countryPhoneExtension: travelerContact?.phones[0]?.countryPhoneExtension,
        number: travelerContact?.phones[0]?.number,
      };
      passengerSMSInformations.push(countryPhone);
    } else {
      // 2.3 入力パラメータ.連絡先情報.phones ≧ 2件の場合、以下の通りに設定する。
      // 2.3.1 NHグループ運航でアメリカ行き便有無=trueの場合
      const isUSFlight = this.isNhGroupOperatedAndArrivalUs;

      // 2.3.3 アメリカの便存在フラグ=trueの場合
      if (isUSFlight) {
        // 	2.3.3.1.	入力パラメータ.連絡先情報.phones[]について、phones[].countryPhoneExtensionとphones[].numberが共に一致する要素の重複を除く
        let mapPassengerSMSInformation = new Map<string, PassengerSMSInformation>();
        travelerContact?.phones.forEach((travelerContactPhone: any) => {
          const passengerSMSInformation: PassengerSMSInformation = {
            countryPhoneExtension: travelerContactPhone?.countryPhoneExtension,
            number: travelerContactPhone?.number,
          };
          const key = travelerContactPhone?.countryPhoneExtension! + '-' + travelerContactPhone?.number!;
          mapPassengerSMSInformation.set(key, passengerSMSInformation);
        });
        // 	2.3.3.2.	重複を除いた要素の件数を繰り返し、国番号 = countryPhoneExtension、電話番号 = numberとしてレコードを搭乗者SMS情報リストに追加する。
        passengerSMSInformations.push(...[...mapPassengerSMSInformation.values()]);
      } else {
        // 2.3.4.	上記以外の場合
        // 2.3.4.1.	国番号 = 入力パラメータ.連絡先情報.phones[0].countryPhoneExtensionを設定する。
        // 2.3.4.2.	電話番号 = 入力パラメータ.連絡先情報.phones[0].numberを設定する。
        // 2.3.4.3.	搭乗者SMS情報リストに国番号と電話番号であるレコードを追加する。
        const passengerSMSInformation: PassengerSMSInformation = {
          countryPhoneExtension: travelerContact?.phones[0]?.countryPhoneExtension,
          number: travelerContact?.phones[0]?.number,
        };
        passengerSMSInformations.push(passengerSMSInformation);
      }
    }

    return passengerSMSInformations;
  }

  /**
   * (共通No.7) Ancillaryサービス名称取得処理
   * @param ssrCode SSRコード
   * @returns Ancillaryサービス名称
   */
  handleCommonNo7(ssrCode: string): string {
    // (共通No.7.1) Ancillaryサービス名称に""(空欄)を設定する。
    let result = '';
    // (共通No.7.2) サービス名称キーに""(空欄)を設定する。
    let serviceNameKey = '';
    // (共通No.7.3) 以下の条件でASWDBマスタの有料サービス情報テーブルと有料サービスSSR情報テーブルから情報を取得する。
    const ancillaryCodeBySsrCode =
      this.serviceRequestPaymentInputMasterState$.value.chargeableServiceSsr?.find(
        // SSRコード=入力パラメータ.SSRコード
        (paidServiceSsr) => paidServiceSsr.ssr_code === ssrCode
      )?.ancillary_code ?? '';
    const paidServiceInfoByCode = this.serviceRequestPaymentInputMasterState$.value.chargeableService?.find(
      (paidService) => paidService.ancillary_code === ancillaryCodeBySsrCode
    );
    // (共通No.7.4) 取得できた場合、サービス名称キーに有料サービスAncillary情報リスト.サービス名称キーを設定する。
    serviceNameKey = paidServiceInfoByCode?.service_name_message_key ?? '';
    // (共通No.7.5) 以下の条件でASWDBマスタの静的文言テーブルから情報を取得する。
    // (共通No.7.6) 取得できた場合、Ancillaryサービス名称に静的文言リスト.メッセージを設定する。
    result = this._staticMsg.transform(serviceNameKey);

    // (共通No.7.7) Ancillaryサービス名称を返却する。
    return result;
  }

  createGetOrderRequest(data?: AswServiceModel): GetOrderRequest {
    const request = {
      credential: {
        firstName: data?.firstName!,
        lastName: data?.lastName!,
      },
      mask: false,
      getServiceCatalogue: false,
      sendReservationInformation: false,
    };
    return {
      ...(data?.orderId && !data?.travelDocumentId && { orderId: data?.orderId }),
      ...(data?.travelDocumentId && { travelDocumentId: data?.travelDocumentId }),
      ...request,
    };
  }

  async getGetOrder(aswService: AswServiceState) {
    const getOrderRequest: GetOrderRequest = this.createGetOrderRequest(aswService);
    const res = this._servicingApiService.ordersGetOrderPost(getOrderRequest);
    const response = await firstValueFrom(res);
    this.pnr$.next(response?.data ?? {});
  }

  async getGetBasicReservationInformation() {
    const param: ApiCommonRequest & GetBasicReservationInformationRequest = {
      commonIgnoreErrorFlg: true,
    };
    const res = this._amcmemberApiService.getBasicReservationInformationPost(param);
    const response = await firstValueFrom(res);
    this.amc$.next(response.data ?? {});
  }

  async postOrdersUpdateServicesPost<UpdateServicesResponse>(
    updateServicesRequest: UpdateServicesRequest
  ): Promise<ApiResponse> {
    return new Promise((resolve) => {
      this._servicingApiService
        .ordersUpdateServicesPost(updateServicesRequest)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            return resolve({ model: response });
          },
          error: (error: ApiErrorResponseModel) => {
            return resolve({ error: error });
          },
        });
    });
  }

  /**
   * (1.11.2) 電話番号国リスト(管轄国・日本)に対して処理する。
   */
  createJpPhoneCountryInfoList(): Array<PhoneNumberCountryType> {
    const userCommonInformation = this._common.aswContextStoreService.aswContextData;
    // (1.11.2.1)	以下の条件でAWSDB（マスタ）から国.国コード(2 レター)、国(国際化).国名称、国.国コード(3 レター) を取得して、電話番号国リスト(管轄国・日本)に追加する。
    const jpPhoneNumberCountryList = this.serviceRequestPaymentInputMasterState$.value.countryI18nPosCountries?.reduce(
      (preValue: Array<PhoneNumberCountryType>, currentValue: CountryI18nPosCountry) => {
        if (
          // 検索条件: POS管轄国.POS国コード＝ユーザ共通情報.POS国コード(2レター) AND
          currentValue.country_2letter_code == userCommonInformation.posCountryCode &&
          // 検索条件: 国.連絡先電話番号国フラグ＝true AND
          currentValue.contact_tel_number_country_flg &&
          // 検索条件: 国(国際化).言語コード＝ユーザ共通情報.言語情報
          currentValue.lang == userCommonInformation.lang
        ) {
          preValue.push({
            country2LetterCode: currentValue.country_2letter_code ?? '',
            country3LetterCode: currentValue.country_3letter_code ?? '',
            countryName: currentValue.country_name ?? '',
          });
        }
        return preValue;
      },
      [] as Array<PhoneNumberCountryType>
    ) as Array<PhoneNumberCountryType>;

    // (1.11.2.2) 電話番号国リスト(管轄国・日本)の中に、国.国コード(2 レター)=“JP”(日本)となる情報が存在しない場合、
    // 以下の内容で日本の国情報を取得し、電話番号国リスト(管轄国・日本)に追加する。
    const isCountryCodeJP = jpPhoneNumberCountryList?.some(
      (jpPhoneNumberCountry) => jpPhoneNumberCountry.country2LetterCode === 'JP'
    );
    if (!isCountryCodeJP) {
      this.serviceRequestPaymentInputMasterState$.value.countryI18nPosCountries?.forEach((country) => {
        if (
          // 国.国コード(2 レター)＝"JP" AND
          country.country_2letter_code === 'JP' &&
          // 国.連絡先電話番号国フラグ＝true
          country.contact_tel_number_country_flg
        )
          jpPhoneNumberCountryList.push({
            country2LetterCode: country.country_2letter_code,
            country3LetterCode: country.country_3letter_code,
            countryName: country.country_name ?? '',
          });
      });
    }

    return jpPhoneNumberCountryList;
  }

  /**
   * (1.11.3) 電話番号国リスト(管轄国・日本以外)に対して処理する。
   */
  createNoJpPhoneCountryInfoList(): Array<PhoneNumberCountryType> {
    const userCommonInformation = this._common.aswContextStoreService.aswContextData;
    // (1.11.3.1) 以下の条件でAWSDB（マスタ）からPOS管轄国.国コード(2レター)を取得して、POS管轄リストに追加する。
    const mPosCountryList: Array<PosCountryAll> =
      this.serviceRequestPaymentInputMasterState$.value.posCountryAll?.filter(
        (posCountry) =>
          // 検索条件: POS管轄国.POS国コード＝ユーザ共通情報.POS国コード(2レター)
          posCountry.pos_country_code === userCommonInformation.posCountryCode
      ) ?? [];
    // (1.11.3.2) 以下の条件でAWSDB（マスタ）から国.国コード(2 レター)、国(国際化).国名称、国.国コード(3 レター) を取得して、電話番号国リスト(管轄国・日本以外)に追加する。
    const noJpPhoneCountryInfoList =
      this.serviceRequestPaymentInputMasterState$.value.countryI18nBy2LetterCodes?.reduce(
        (preValue: Array<PhoneNumberCountryType>, currentValue: CountryI18nBy2LetterCode) => {
          if (
            // 検索条件: 国.国コード(2 レター) NOT IN POS管轄リスト
            !mPosCountryList.some((item) => item.country_2letter_code === currentValue.country_2letter_code) &&
            // 検索条件: 国.国コード(2 レター)≠“JP”
            currentValue.country_2letter_code !== 'JP' &&
            // 検索条件: 国.連絡先電話番号国フラグ＝true
            currentValue.contact_tel_number_country_flg
          ) {
            preValue.push({
              country2LetterCode: currentValue.country_2letter_code,
              country3LetterCode: currentValue.country_3letter_code,
              countryName: currentValue.country_name ?? '',
            });
          }
          return preValue;
        },
        [] as Array<PhoneNumberCountryType>
      ) as Array<PhoneNumberCountryType>;

    return noJpPhoneCountryInfoList;
  }

  /**
   * (1.11.4) アメリカ・カナダについては、ASWDB(マスタ)で管理している電話番号国用の名称で表示するために、以下の処理にて電話番号国名称を上書きする。
   */
  createUsCanadaPhoneCountryInfoList(): Array<MListData> {
    const userCommonInformation = this._common.aswContextStoreService.aswContextData;
    // (1.11.4.1) 以下の条件にて取得したリストを、電話番号対象国(リスト)とする。
    let countryList: Array<CountryI18nBy2LetterCode> =
      this.serviceRequestPaymentInputMasterState$.value.countryI18nBy2LetterCodes?.filter(
        (country) =>
          // 検索条件: 国.連絡先電話番号国フラグ＝true
          country.contact_tel_number_country_flg &&
          // 検索条件: 国(国際化).言語コード＝ユーザ共通情報.言語情報
          country.lang === userCommonInformation.lang
      ) ?? [];
    // (1.11.4.2) 電話番号対象国(マップ)に対して処理する。
    // <以下、電話番号対象国(リスト)の件数分繰り返し>
    countryList?.forEach((item) => {
      // (1.11.4.2.1) キー = 国.国コード(2 レター) 、値 = 国.国際電話国番号であるレコードを電話番号対象国(マップ)に追加する。
      // <ここまで、電話番号対象国(リスト)の件数分繰り返し>
      this.countryMap.set(item.country_2letter_code, item.international_tel_country_code);
    });

    // (1.11.4.3) 汎用マスターデータ(リスト)を取得する。
    const master: Array<MListData> =
      this.serviceRequestPaymentInputMasterState$.value.listData?.filter(
        (data) =>
          // 検索条件: データコード＝“PD_019”
          data.data_code === 'PD_019' &&
          // 検索条件: 言語コード=ユーザ共通情報.言語情報
          data.lang === userCommonInformation.lang
      ) ?? [];

    return master;
  }

  /**
   * [■リスト定義No.3]
   * [■リスト定義No.4]
   */
  createPhoneCountryInfoList(): void {
    // (1.11.2) 電話番号国リスト(管轄国・日本)に対して処理する。
    this.jpPhoneNumberCountryList = this.createJpPhoneCountryInfoList();
    // (1.11.3) 電話番号国リスト(管轄国・日本以外)に対して処理する。
    this.noJpPhoneNumberCountryList = this.createNoJpPhoneCountryInfoList();
    // (1.11.4) アメリカ・カナダについては、ASWDB(マスタ)で管理している電話番号国用の名称で表示するために、以下の処理にて電話番号国名称を上書きする。
    const listData = this.createUsCanadaPhoneCountryInfoList();
    // ＜以下、汎用マスターデータ(リスト)の件数分繰り返し＞
    listData?.forEach((item) => {
      // ＜以下、電話番号国リスト(管轄国・日本)の件数分繰り返し＞
      // ＜ここまで、電話番号国リスト(管轄国・日本)の件数分繰り返し＞
      this.jpPhoneNumberCountryList?.forEach((jpPhoneNumberCountry) => {
        // (1.11.4.4) 当該汎用マスターデータ(リスト).value値＝国.国コード(3レター)となる国(国際化).国名称に当該汎用マスターデータ(リスト).表示内容を上書きする。
        if (item.value === jpPhoneNumberCountry.country3LetterCode) {
          jpPhoneNumberCountry.countryName = item.display_content;
        }
      });
      // ＜以下、電話番号国リスト(管轄国・日本以外)の件数分繰り返し＞

      // ＜ここまで、電話番号国リスト(管轄国・日本以外)の件数分繰り返し＞
      this.noJpPhoneNumberCountryList?.forEach((noJpPhoneNumberCountry) => {
        // (1.11.4.5) 当該汎用マスターデータ(リスト).value値＝国.国コード(3レター)となる国(国際化).国名称に当該汎用マスターデータ(リスト).表示内容を上書きする。
        if (item.value === noJpPhoneNumberCountry.country3LetterCode) {
          noJpPhoneNumberCountry.countryName = item.display_content;
        }
      });
      // ＜ここまで、汎用マスターデータ(リスト)の件数分繰り返し＞
    });

    /**
     * 値: “”(空欄)
     * ラベル: 未選択である旨の文言
     * ソート順: -
     */
    this.phoneNumberCountryList.push({
      country2LetterCode: '',
      country3LetterCode: '',
      countryName: this._staticMsg.transform('placeholder.pleaseSelect2'),
    });

    /**
     * 値: 国.国コード(2レター)
     * ラベル: 国(国際化).国名称(※3)
     * ソート順: 連絡先電話番号国の管轄国(日本含む)の順(※1)
     */
    this.jpPhoneNumberCountryList?.forEach((item) => {
      this.phoneNumberCountryList.push({
        country2LetterCode: item.country2LetterCode,
        country3LetterCode: item.country3LetterCode,
        countryName: item.countryName,
      });
    });

    /**
     * 値: “-”
     * ラベル: 区切り線
     * ソート順:
     */
    this.phoneNumberCountryList.push({
      country2LetterCode: '',
      country3LetterCode: '',
      countryName: this._staticMsg.transform('pulldown.separatorHyphen'),
    });

    /**
     * 値: 国.国コード(2レター)
     * ラベル: 国(国際化).国名称(※3)
     * ソート順: 国(国際化).表示順(昇順) (管轄国、日本以外の連絡先電話番号国のリスト) (※2)
     */
    this.noJpPhoneNumberCountryList?.forEach((item) => {
      this.phoneNumberCountryList.push({
        country2LetterCode: item.country2LetterCode,
        country3LetterCode: item.country3LetterCode,
        countryName: item.countryName,
      });
    });
  }

  removeControlsByMethod(paymentMethod: string) {
    if (paymentMethod !== PaymentMethodsType.CREDIT_CARD) {
      REMOVE_VALIDATIONS_FORMS.forEach((key) => {
        this.paymentInputFG.removeControl(key);
      });
    }
  }

  /**
   * [1.9] 領収書の発行名義
   * @returns boolean
   */
  isDisplayIssuerReceipt(): boolean {
    return (
      this.paymentMethod$.value === PaymentMethodsType.CREDIT_CARD &&
      this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP'
    );
  }

  destroy() {
    /* override abstract destroy function of base class */
  }
}
